import { useCallback, useState } from "react";
import { Button, Stack, styled } from "@mui/material";
import { useField, useForm } from "react-final-form";
import { CollectionItem, createListCollection } from "@ark-ui/react";

import { useTranslation } from "@/lib/i18n";
import { TextField } from "@/features/shared/components";
import { EdrObservationsMapLayer } from "@/lib/api";
import Select from "@/components/select/select";

interface EDRParameterSetProps {
  name: string;
  parameters: CollectionItem[];
  initialValue: EdrObservationsMapLayer;
}

const EDRParameterSet = ({ name, parameters, initialValue }: EDRParameterSetProps) => {
  const { t } = useTranslation();
  const form = useForm();

  const [parameterCount, setParameterCount] = useState(
    initialValue.parameter_sets?.length ?? 1,
  );

  const removeParameter = useCallback(
    (index: number) => {
      for (let i = index; i < parameterCount - 1; i++) {
        // move each parameter set 1 index up
        const titleValue = form.getFieldState(`${name}.parameter_sets[${i + 1}].title`);
        const paramsValue = form.getFieldState(
          `${name}.parameter_sets[${i + 1}].parameters[]`,
        );
        form.change(`${name}.parameter_sets[${i}].title`, titleValue?.value);
        form.change(`${name}.parameter_sets[${i}].parameters[]`, paramsValue?.value);
      }

      // empty last parameter set and update count
      form.change(`${name}.parameter_sets[${parameterCount - 1}]`, undefined);
      setParameterCount((previousCount) => previousCount - 1);
    },
    [parameterCount],
  );

  return (
    <>
      {[...new Array(parameterCount)].map((_, parameterIndex) => (
        <StyledParameterSet key={parameterIndex} padding={2} gap={2} mt={1}>
          <TextField
            name={`${name}.parameter_sets[${parameterIndex}].title`}
            label={t("admin.widgets.fields.map.edr_observations.parameter_name")}
          />
          <ParameterSelector
            name={`${name}.parameter_sets[${parameterIndex}].parameters[]`}
            parameters={parameters}
          />
          {parameterCount > 1 && (
            <Button onClick={() => removeParameter(parameterIndex)}>
              Verwijder parameter
            </Button>
          )}
        </StyledParameterSet>
      ))}
      <Button onClick={() => setParameterCount((previousCount) => previousCount + 1)}>
        Voeg parameter toe
      </Button>
    </>
  );
};

interface ParameterSelectorProps {
  name: string;
  parameters: CollectionItem[];
}

const ParameterSelector = ({ name, parameters }: ParameterSelectorProps) => {
  const { t } = useTranslation();
  const form = useForm();
  const parametersField = useField(name, {
    validate: (value) =>
      Array.isArray(value) && value.length <= 0 ? t("form_validation.required") : undefined,
    multiple: true,
  });

  const collection = createListCollection({
    items: parameters,
    groupBy: (item) => item.group,
  });

  return (
    <Select
      placeholder="Selecteer één of meerdere parameters"
      collection={collection}
      value={parametersField.input.value}
      onValueChange={(value) =>
        form.change(parametersField.input.name, Array.isArray(value) ? value : [value])
      }
      error={parametersField.meta.touched && parametersField.meta.error}
      multiple
    />
  );
};

const StyledParameterSet = styled(Stack)`
  background: rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.1);
  padding-top: 0;
`;

export default EDRParameterSet;
